/**
 * Copyright Sourcepole AG
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from 'react';
import {connect} from 'react-redux';

import PropTypes from 'prop-types';

import {LayerRole, addLayer, addLayerFeatures, changeLayerProperty} from '../actions/layers';
import {setSnappingConfig} from '../actions/map';
import {changeRedliningState, resetRedliningState} from '../actions/redlining';
import Icon from '../components/Icon';
import MeasureSwitcher from '../components/MeasureSwitcher';
import TaskBar from '../components/TaskBar';
import ButtonBar from '../components/widgets/ButtonBar';
import ColorButton from '../components/widgets/ColorButton';
import ComboBox from '../components/widgets/ComboBox';
import MenuButton from '../components/widgets/MenuButton';
import NumberInput from '../components/widgets/NumberInput';
import VectorLayerPicker from '../components/widgets/VectorLayerPicker';
import ConfigUtils from '../utils/ConfigUtils';
import {END_MARKERS} from '../utils/FeatureStyles';
import FileImportUtils from '../utils/FileImportUtils';
import LocaleUtils from '../utils/LocaleUtils';

import './style/Redlining.css';


const POINT_SHAPE_BUTTONS = {
    circle: "Point",
    square: "PointSquare",
    star: "PointStar"
};

/**
 * Allows drawing figures and text labels on the map.
 */
class Redlining extends React.Component {
    static propTypes = {
        addLayer: PropTypes.func,
        addLayerFeatures: PropTypes.func,
        /** Whether to allow labeling geometric figures. */
        allowGeometryLabels: PropTypes.bool,
        changeLayerProperty: PropTypes.func,
        changeRedliningState: PropTypes.func,
        /** Default area unit. Options: `metric`, `imperial`, `sqm`, `ha`, `sqkm`, `sqft`, `acre`, `sqmi` */
        defaultAreaUnit: PropTypes.string,
        /** Default border color. In format `[r, g, b, a]`. */
        defaultBorderColor: PropTypes.array,
        /** Default fill color. In format `[r, g, b, a]`. */
        defaultFillColor: PropTypes.array,
        /** Default length unit. Options: `metric`, `imperial`, `m`, `km`, `ft`, `mi` */
        defaultLengthUnit: PropTypes.string,
        /** Default text fill color. In format `[r, g, b, a]`. */
        defaultTextFillColor: PropTypes.array,
        /** Default text outline color. In format `[r, g, b, a]`. */
        defaultTextOutlineColor: PropTypes.array,
        /** Tools to hide. Available tools: `PointSquare`, `PointStar`, `Circle`, `Ellipse`, `Square`, `Box`, `HandDrawing`, `Transform`, `NumericInput`, `Buffer`, `FeatureLabel`, `Export`. */
        hiddenTools: PropTypes.array,
        layers: PropTypes.array,
        mapCrs: PropTypes.string,
        plugins: PropTypes.object,
        /** Predefined border colors. In format `[[r, g, b, a], ...]`. */
        predefinedBorderColors: PropTypes.arrayOf(PropTypes.array),
        /** Predefined dash patterns. In format `[<dash-array>, ...]`, where a dash-array is list of alternating dash and gap widths, i.e. `[8 4]` for long dashes followed by shorter gaps. */
        predefinedDashPatterns: PropTypes.arrayOf(PropTypes.array),
        /** Predefined fill colors. In format `[[r, g, b, a], ...]`. */
        predefinedFillColors: PropTypes.arrayOf(PropTypes.array),
        redlining: PropTypes.object,
        resetRedliningState: PropTypes.func,
        setCurrentTask: PropTypes.func,
        setSnappingConfig: PropTypes.func,
        /** Whether snapping is available when editing. */
        snapping: PropTypes.bool,
        /** Whether snapping is enabled by default when editing.
         *  Either `edge`, `vertex`, `intersection`, `perpendicular`, a combination of these as an array, or `false` or `true` (i.e. all snapping modes disabled or enabled). */
        snappingActive: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.array])
    };
    static defaultProps = {
        allowGeometryLabels: true,
        hiddenTools: [],
        snapping: true,
        snappingActive: true,
        plugins: [],
        defaultBorderColor: [255, 0, 0, 1],
        defaultFillColor: [255, 255, 255, 1],
        defaultTextFillColor: [0, 0, 0, 1],
        defaultTextOutlineColor: [255, 255, 255, 1],
        defaultAreaUnit: 'metric',
        defaultLengthUnit: 'metric',
        predefinedDashPatterns: [[], [4], [8, 8], [1, 8], [8, 8, 1, 8]]
    };
    state = {
        selectText: false
    };
    constructor(props) {
        super(props);
        this.labelInput = null;
        this.dashIcons = {};
        this.fileinput = document.createElement("input");
        this.fileinput.type = "file";
        this.fileinput.accept = "application/json,application/vnd.google-earth.kml+xml";
        this.fileinput.addEventListener("change", this.fileSelected);
    }
    componentDidMount() {
        this.componentDidUpdate({redlining: {}});
    }
    componentDidUpdate(prevProps) {
        if (
            this.props.defaultBorderColor !== prevProps.defaultBorderColor ||
            this.props.defaultFillColor !== prevProps.defaultFillColor ||
            this.props.defaultLengthUnit !== prevProps.defaultLengthUnit ||
            this.props.defaultAreaUnit !== prevProps.defaultAreaUnit ||
            this.props.defaultTextFillColor !== prevProps.defaultTextFillColor ||
            this.props.defaultTextOutlineColor !== prevProps.defaultTextOutlineColor
        ) {
            this.props.changeRedliningState(this.redliningStateDefaults());
        }
        if (prevProps.redlining.geomType !== this.props.redlining.geomType && this.props.redlining.geomType === 'Text' && !this.state.selectText) {
            this.setState({selectText: true});
        }
        if (!this.props.layers.find(layer => layer.id === this.props.redlining.layer)) {
            const vectorLayers = this.props.layers.filter(layer => layer.type === "vector" && layer.role === LayerRole.USERLAYER && !layer.readonly && !layer.layertreehidden && !layer.externallyManaged);
            if (vectorLayers.length >= 1) {
                this.props.changeRedliningState({layer: vectorLayers[0].id, layerTitle: vectorLayers[0].title});
            } else if (this.props.redlining.layer !== 'redlining') {
                this.props.changeRedliningState({layer: 'redlining', layerTitle: LocaleUtils.tr('redlining.layertitle')});
            }
        }
        if (this.props.predefinedDashPatterns !== prevProps.predefinedDashPatterns) {
            this.generateDashIcons();
        }
    }
    onShow = (mode, data) => {
        this.props.changeRedliningState({action: mode ?? 'Pick', geomType: data?.geomType ?? null, ...this.redliningStateDefaults()});
        this.props.setSnappingConfig(this.props.snapping, this.props.snappingActive);
        let layer = null;
        if (data && data.layerId) {
            layer = this.props.layers.find(l => l.id === data.layerId);
        } else {
            layer = this.props.layers.find(l => l.id === this.props.redlining.layer);
        }
        if (layer) {
            this.changeRedliningLayer(layer);
        }
    };
    onHide = () => {
        this.props.resetRedliningState();
    };
    redliningStateDefaults = () => {
        return {
            style: {
                ...this.props.redlining.style,
                borderColor: this.props.defaultBorderColor,
                fillColor: this.props.defaultFillColor,
                textOutlineColor: this.props.defaultTextOutlineColor,
                textFillColor: this.props.defaultTextFillColor
            },
            lenUnit: this.props.defaultLengthUnit,
            areaUnit: this.props.defaultAreaUnit
        };
    };
    generateDashIcons = () => {
        this.dashIcons = this.props.predefinedDashPatterns.reduce((res, pattern) => {
            const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="16"><line x1="0" y1="8" x2="32" y2="8" stroke="black" stroke-width="2" stroke-dasharray="${pattern.join(" ")}"></line></svg>`;
            res[pattern.join(":")] = `data:image/svg+xml;base64,${btoa(svg)}`;
            return res;
        }, {});
    };
    updateRedliningStyle = (diff) => {
        const newStyle = {...this.props.redlining.style, ...diff};
        this.props.changeRedliningState({style: newStyle});
    };
    renderBody = () => {
        const toolEnabled = (tool) => !this.props.hiddenTools.includes(tool);
        let activeButton = this.props.redlining.action === "Draw" ? this.props.redlining.geomType : this.props.redlining.action;
        if (activeButton === "Point") {
            // All point shapes share the Point geomType, so the active entry is determined by the current shape
            const pointShape = this.props.redlining.style.pointShape;
            activeButton = Object.hasOwn(POINT_SHAPE_BUTTONS, pointShape) ? POINT_SHAPE_BUTTONS[pointShape] : "Point";
        }
        let drawButtons = [
            [
                {key: "Point", tooltip: LocaleUtils.tr("common.point"), icon: "point", data: {action: "Draw", geomType: "Point", text: "", style: {pointShape: "circle"}}},
                toolEnabled("PointSquare") ? {key: "PointSquare", tooltip: LocaleUtils.tr("common.square"), icon: "square", data: {action: "Draw", geomType: "Point", text: "", style: {pointShape: "square"}}} : null,
                toolEnabled("PointStar") ? {key: "PointStar", tooltip: LocaleUtils.tr("common.star"), icon: "star", data: {action: "Draw", geomType: "Point", text: "", style: {pointShape: "star"}}} : null
            ].filter(Boolean),
            {key: "LineString", tooltip: LocaleUtils.tr("common.line"), icon: "line", data: {action: "Draw", geomType: "LineString", text: ""}},
            [
                {key: "Polygon", tooltip: LocaleUtils.tr("common.polygon"), icon: "polygon", data: {action: "Draw", geomType: "Polygon", text: ""}},
                toolEnabled("Circle") ? {key: "Circle", tooltip: LocaleUtils.tr("common.circle"), icon: "circle", data: {action: "Draw", geomType: "Circle", text: ""}} : null,
                toolEnabled("Ellipse") ? {key: "Ellipse", tooltip: LocaleUtils.tr("common.ellipse"), icon: "ellipse", data: {action: "Draw", geomType: "Ellipse", text: ""}} : null,
                toolEnabled("Square") ? {key: "Square", tooltip: LocaleUtils.tr("common.square"), icon: "box", data: {action: "Draw", geomType: "Square", text: ""}} : null,
                toolEnabled("Box") ? {key: "Box", tooltip: LocaleUtils.tr("common.rectangle"), icon: "rect", data: {action: "Draw", geomType: "Box", text: ""}} : null
            ].filter(Boolean),
            {key: "Text", tooltip: LocaleUtils.tr("common.text"), icon: "text", data: {action: "Draw", geomType: "Text", text: "", measurements: false}}
        ];
        if (ConfigUtils.isMobile()) {
            drawButtons = [drawButtons.flat()];
        }
        const activeFreeHand = this.props.redlining.freehand ? "HandDrawing" : null;
        const freehandButtons = toolEnabled("HandWriting") ? [{
            key: "HandDrawing", tooltip: LocaleUtils.tr("redlining.freehand"), icon: "freehand",
            data: {action: "Draw", geomType: this.props.redlining.geomType, text: "", freehand: !this.props.redlining.freehand},
            disabled: (this.props.redlining.geomType !== "LineString" && this.props.redlining.geomType !== "Polygon")
        }] : [];
        const editButtons = [
            {key: "Pick", tooltip: LocaleUtils.tr("common.pick"), icon: "nodetool", data: {action: "Pick", geomType: null, text: ""}},
            toolEnabled("Transform") ? {key: "Transform", tooltip: LocaleUtils.tr("redlining.transform"), icon: "transformtool", data: {action: "Transform", geomType: null, text: ""}} : null,
            toolEnabled("Clone") ? {key: "Clone", tooltip: LocaleUtils.tr("common.clone"), icon: "clone", data: {action: "Clone", geomType: null}, disabled: !this.props.redlining.selectedFeature} : null,
            {key: "Delete", tooltip: LocaleUtils.tr("common.delete"), icon: "trash", data: {action: "Delete", geomType: null}, disabled: !this.props.redlining.selectedFeature}
        ].filter(Boolean);
        const extraButtons = toolEnabled("NumericInput") ? [
            {key: "NumericInput", tooltip: LocaleUtils.tr("redlining.numericinput"), icon: "numericinput"},
            {key: "FeatureAttributes", tooltip: LocaleUtils.tr("redlining.attributes"), icon: "list-alt"}
        ] : [];
        for (const plugin of Object.values(this.props.plugins || {})) {
            if (toolEnabled(plugin.cfg.key)) {
                const buttonGroup = plugin.cfg.tooltype === 'draw' ? drawButtons : editButtons;
                buttonGroup.push({
                    ...plugin.cfg,
                    tooltip: plugin.cfg.tooltip ? LocaleUtils.tr(plugin.cfg.tooltip) : undefined
                });
            }
        }
        let vectorLayers = this.props.layers.filter(layer => layer.type === "vector" && layer.role === LayerRole.USERLAYER && !layer.readonly && !layer.layertreehidden && !layer.externallyManaged);
        // Ensure list always contains at least a "Redlining" layer
        if (vectorLayers.length === 0) {
            vectorLayers = [{id: 'redlining', title: LocaleUtils.tr('redlining.layertitle')}, ...vectorLayers];
        }
        const haveLayer = (this.props.layers.find(l => l.id === this.props.redlining.layer)?.features?.length || 0) > 0;

        const activePlugin = Object.values(this.props.plugins || {}).find(plugin => plugin.cfg.key === this.props.redlining.action);
        const controls = activePlugin ? (<activePlugin.controls />) : this.renderStandardControls();

        return (
            <div>
                <div className="redlining-controlsbar">
                    <div className="redlining-groupcontrol">
                        <div>{LocaleUtils.tr("redlining.layer")}</div>
                        <VectorLayerPicker
                            addLayer={this.props.addLayer} layers={vectorLayers}
                            onChange={this.changeRedliningLayer} value={this.props.redlining.layer} />
                    </div>
                    <div className="redlining-groupcontrol">
                        <div>{LocaleUtils.tr("redlining.draw")}</div>
                        <div className="controlgroup">
                            <ButtonBar active={activeButton} buttons={drawButtons} onClick={(key, data) => this.actionChanged(data)} />
                            {this.props.redlining.action === "Draw" && (this.props.redlining.geomType === "LineString" || this.props.redlining.geomType === "Polygon") ?
                                <ButtonBar active={activeFreeHand} buttons={freehandButtons} onClick={(key, data) => this.actionChanged(data)} /> : null
                            }
                        </div>
                    </div>
                    <div className="redlining-groupcontrol">
                        <div>{LocaleUtils.tr("redlining.edit")}</div>
                        <ButtonBar active={activeButton} buttons={editButtons} onClick={(key, data) => this.actionChanged(data)} />
                    </div>
                    <div className="redlining-groupcontrol">
                        <div>&nbsp;</div>
                        <ButtonBar active={this.props.redlining.extraAction} buttons={extraButtons} onClick={(key) => this.props.changeRedliningState({extraAction: key})} />
                    </div>
                    {toolEnabled("Export") ? (
                        <div className="redlining-groupcontrol">
                            <div>&nbsp;</div>
                            <MenuButton className="redlining-export-menu" menuIcon="export" onActivate={this.importExport} tooltip={LocaleUtils.tr("common.export")}>
                                <div className="redlining-export-menu-entry" value="import:geojson">{LocaleUtils.tr("common.import")}</div>
                                <div className="redlining-export-menu-entry" disabled={!haveLayer} value="export:geojson">{LocaleUtils.tr("common.export")} GeoJSON</div>
                                <div className="redlining-export-menu-entry" disabled={!haveLayer} value="export:kml">{LocaleUtils.tr("common.export")} KML</div>
                            </MenuButton>
                        </div>
                    ) : null}
                </div>
                {controls}
            </div>
        );
    };
    importExport = (key) => {
        const parts = key.split(":");
        if (parts[0] === "import") {
            this.fileinput.click();
        } else if (parts[0] === "export") {
            this.props.changeRedliningState({action: 'Export', format: parts[1]});
        }
    };
    fileSelected = (ev) => {
        const reader = new FileReader();
        reader.readAsText(ev.target.files[0]);
        reader.onload = () => {
            const file = ev.target.files[0];
            try {
                if (file.type === "application/json") {
                    FileImportUtils.addGeoJSONLayer(file.name, JSON.parse(reader.result), this.props.addLayerFeatures);
                } else if (file.type === "application/vnd.google-earth.kml+xml") {
                    FileImportUtils.addKMLLayer(file.name, reader.result, this.props.addLayerFeatures);
                }
            } catch {
                /* eslint-disable-next-line no-alert */
                alert(LocaleUtils.tr("common.dataloadfailed"));
            }
        };
    };
    renderStandardControls = () => {
        let sizeLabel = LocaleUtils.tr("common.line");
        let showDash = true;
        if (["Text", "Point"].includes(this.props.redlining.geomType)) {
            sizeLabel = LocaleUtils.tr("redlining.size");
            showDash = false;
        }
        let labelPlaceholder = LocaleUtils.tr("redlining.label");
        if (this.props.redlining.geomType === "Text") {
            labelPlaceholder = LocaleUtils.tr("common.text");
        }
        if (this.props.redlining.action !== 'Draw' && !this.props.redlining.selectedFeature) {
            if (this.props.redlining.action === 'Transform') {
                return (
                    <div className="redlining-message">
                        {LocaleUtils.tr("redlining.ctrlhint")}
                    </div>
                );
            } else {
                return null;
            }
        }

        return (
            <div className="redlining-controlsbar">
                <div className="redlining-control">
                    <Icon className="redlining-control-icon" icon="pen" size="large" />
                    {this.props.redlining.geomType === 'Text' ? (
                        <ColorButton color={this.props.redlining.style.textOutlineColor} defaultColors={this.props.predefinedBorderColors} onColorChanged={(color) => this.updateRedliningStyle({textOutlineColor: color})} />
                    ) : (
                        <ColorButton color={this.props.redlining.style.borderColor} defaultColors={this.props.predefinedBorderColors} onColorChanged={(color) => this.updateRedliningStyle({borderColor: color})} />
                    )}
                </div>
                {this.props.redlining.geomType === 'LineString' ? null : (
                    <div className="redlining-control">
                        <Icon className="redlining-control-icon" icon="fill" size="large" />
                        {this.props.redlining.geomType === 'Text' ? (
                            <ColorButton color={this.props.redlining.style.textFillColor} defaultColors={this.props.predefinedFillColors} onColorChanged={(color) => this.updateRedliningStyle({textFillColor: color})} />
                        ) : (
                            <ColorButton color={this.props.redlining.style.fillColor} defaultColors={this.props.predefinedFillColors} onColorChanged={(color) => this.updateRedliningStyle({fillColor: color})} />
                        )}
                    </div>
                )}
                <div className="redlining-control">
                    <span>{sizeLabel}:&nbsp;</span>
                    <NumberInput max={99} min={0} mobile
                        onChange={(nr) => this.updateRedliningStyle({size: nr})}
                        value={this.props.redlining.style.size}/>
                    {showDash ? (
                        <ComboBox onChange={value => this.updateRedliningStyle({strokeDash: value.split(":").filter(Boolean).map(Number)})} value={this.props.redlining.style.strokeDash.join(":")}>
                            {this.props.predefinedDashPatterns.map(pattern => {
                                const value = pattern.join(":");
                                return (
                                    <div className="redlining-dash-combo-entry" key={value} value={value}>
                                        <img src={this.dashIcons[value]} />
                                    </div>
                                );
                            })}
                        </ComboBox>
                    ) : null}
                </div>
                {this.props.redlining.geomType === 'LineString' ? (
                    <div className="redlining-control">
                        <span>{LocaleUtils.tr("redlining.markers")}:&nbsp;</span>
                        <div className="controlgroup">
                            <ComboBox className="redlining-marker-combo" onChange={value => this.updateRedliningStyle({headmarker: value})} value={this.props.redlining.style.headmarker || ""}>
                                <div className="redlining-marker-combo-entry" value="" />
                                {Object.entries(END_MARKERS).map(([key, params]) => (
                                    <div className="redlining-marker-combo-entry" key={key} value={key}>
                                        <img src={params.src} style={{transform: 'rotate(' + params.baserotation + 'deg)'}}/>
                                    </div>
                                ))}
                            </ComboBox>
                            <ComboBox className="redlining-marker-combo" onChange={value => this.updateRedliningStyle({tailmarker: value})} value={this.props.redlining.style.tailmarker || ""}>
                                <div className="redlining-marker-combo-entry" value="" />
                                {Object.entries(END_MARKERS).map(([key, params]) => (
                                    <div className="redlining-marker-combo-entry" key={key} value={key}>
                                        <img src={params.src} style={{transform: 'rotate(' + (180 + params.baserotation) + 'deg)'}}/>
                                    </div>
                                ))}
                            </ComboBox>
                        </div>
                    </div>
                ) : null}
                <div className="redlining-control redlining-control-fill">
                    <div className={"controlgroup" + (this.props.redlining.showmeasurements ? " redlining-control-fill" : "")}>
                        {this.props.redlining.geomType !== 'Text' && this.props.allowGeometryLabels ? (
                            <MeasureSwitcher
                                changeMeasureState={(diff) => this.props.changeRedliningState({...diff, text: ''})}
                                geomType={this.props.redlining.geomType}
                                measureState={this.props.redlining}
                            />
                        ) : null}
                        {(this.props.redlining.geomType === 'Text' || this.props.allowGeometryLabels) && !this.props.redlining.showmeasurements ? (
                            <input className="controlgroup-fillitem" onChange={(ev) => this.updateRedliningStyle({text: ev.target.value})} placeholder={labelPlaceholder} readOnly={this.props.redlining.showmeasurements} ref={el => this.setLabelRef(el)} type="text" value={this.props.redlining.style.text}/>
                        ) : null}
                    </div>
                </div>
            </div>
        );
    };
    render() {
        return (
            <TaskBar onHide={this.onHide} onShow={this.onShow} task="Redlining">
                {() => ({
                    body: this.renderBody()
                })}
            </TaskBar>
        );
    }
    setLabelRef = (el) => {
        this.labelInput = el;
        if (el && this.state.selectText) {
            el.focus();
            el.select();
            this.setState({selectText: false});
        }
    };
    actionChanged = (data) => {
        if (data.action === "Draw" && data.geomType === "Text") {
            data = {...data, style: {...data.style, text: LocaleUtils.tr("common.text")}};
        } else if (!this.props.allowGeometryLabels || this.props.redlining.geomType === "Text" && data.action === "Draw" && data.geomType !== "Text") {
            // Merge, so the style carried by the button (the point shape) is not dropped
            data = {...data, style: {...data.style, text: ''}};
        }
        this.props.changeRedliningState(data);
    };
    changeRedliningLayer = (layer) => {
        const action = ["Draw", "Pick", "Transform"].includes(this.props.redlining.action) ? this.props.redlining.action : "Pick";
        this.props.changeRedliningState({layer: layer.id, layerTitle: layer.title, action: action});
        // Ensure layer is visible
        this.props.changeLayerProperty(layer.id, "visibility", true);
    };
}

export default (plugins) => {
    return connect((state) => ({
        layers: state.layers.flat,
        redlining: state.redlining,
        mapCrs: state.map.projection,
        plugins: plugins
    }), {
        changeRedliningState: changeRedliningState,
        changeLayerProperty: changeLayerProperty,
        addLayer: addLayer,
        addLayerFeatures: addLayerFeatures,
        resetRedliningState: resetRedliningState,
        setSnappingConfig: setSnappingConfig
    })(Redlining);
};
